import { CreditCardTier } from 'types/dto/profile';
import dayjs from 'dayjs';
import type { FormInstance } from 'antd';
import type { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import type { CommonOption } from 'types/common';
import { notification } from 'antd';
import { NOTIFICATION_DURATION, MONTH_SHORT_FORMAT, DAY_SHORT_FORMAT, TIME_FORMAT, DATE_WITH_DAY_MM_DD_FORMAT } from 'constants/consts';
import { OrderStatus } from 'types/dto/order';
import { OrderStatusObj } from 'constants/consts';
import { SessionDTO } from 'types/dto/login';

export function derivePathKeys(pathname: string, base: string) {
  const normalizedBase = base == null || base === 'undefined' ? '/' : base;
  const pathWithoutBase = normalizedBase === '/'
    ? pathname
    : pathname.startsWith(normalizedBase)
      ? pathname.slice(normalizedBase.length)
      : pathname;

  const parts = pathWithoutBase.split('/').filter(Boolean);

  return {
    parent: parts[0],
    child: parts[1],
  };
}

export const getCssVar = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();


export const getTierColor = (tier: CreditCardTier) => {
  switch (tier) {
  case 'blue':
    return getCssVar('--vls-tier-blue');
  case 'silver':
    return getCssVar('--vls-tier-silver');
  case 'gold':
    return getCssVar('--vls-tier-gold');
  case 'platinum':
    return getCssVar('--vls-tier-platinum');
  default:
    return 'white';
  }
};

// Helper function to convert month number to short month name
export const getMonthNameByNumber = (month: number): string => {
  return dayjs().month(month - 1).format(MONTH_SHORT_FORMAT);
};

// Helper function to convert day of week number to short day name
// dayOfWeek:0 = Sunday, 1 = Monday, 2 = Tuesday, ...,
export const getDayNameByNumber = (dayOfWeek: number): string => {
  return dayjs().day(dayOfWeek).format(DAY_SHORT_FORMAT);
};

// Helper function to format date string with day of week
// dateString format: YYYY-MM-DD
// returns format: MM-DD-YYYY (ddd) e.g., "01-15-2025 (Wed)"
export const formatDateWithDay = (dateString: string): string => {
  return dayjs(dateString).format(DATE_WITH_DAY_MM_DD_FORMAT);
};

export const formatTime = (timeString: string): string => {
  return dayjs(timeString).format(TIME_FORMAT);
};

// Mask phone number - show first 2 and last 2 digits, mask middle 4
export const maskPhoneNumber = (phone: string) => {
  // console.log('masking phone number: ', phone);
  if (!phone || phone.length !== 8) return phone;
  return `${phone.slice(0, 2)}****${phone.slice(6)}`;
};

/**
 * Scroll to the first validation error field in a form
 * @param error - The error object from form validation
 * @param form - The Ant Design Form instance
 */
export const scrollToFirstErrorField = (error: unknown, form: FormInstance) => {
  const validationError = error as ValidateErrorEntity;
  if (validationError?.errorFields?.[0]?.name) {
    // console.log('scrollToFirstErrorField: ', validationError.errorFields[0].name);
    form.scrollToField(validationError.errorFields[0].name, {
      behavior: 'smooth',
      block: 'center',
      scrollMode: 'if-needed',
    });
  }
};

/**
 * Convert an array of {value, label} objects to CommonOption array
 * @param data - Array of objects with value and label properties
 * @returns Array of CommonOption objects with value, label, and disabled flag
 */
export const convertObjectToOptions = (data: Array<{ value: string; label: string }>): CommonOption[] => {
  return data.map(item => ({
    value: item.value,
    label: item.label,
    disabled: String(item.label).startsWith('-'),
  }));
};

export const convertToOptions = (data: Record<string, unknown>): CommonOption[] => {
  return Object.entries(data).map(([key, value]) => {
    const labelValue = String(value ?? '');

    return {
      value: key,
      label: labelValue,
      disabled: labelValue.startsWith('-'),
    };
  });
};

export const maskEmail = (email:string | undefined) => {
  if (!email) return '';
  const [localPart, domain] = email.split('@');
  const maskedLocal = `${localPart.slice(0, 1)}***${localPart.slice(-1)}`;
  return `${maskedLocal}@${domain}`;
};


export const showErrorNotification = (message: string, error?: Error) => {
  notification.error({
    message,
    description: error?.message,
    duration: NOTIFICATION_DURATION
  });
};

export const isActiveStatus = (status: OrderStatus) => {
  return status === OrderStatusObj.RESERVED || status === OrderStatusObj.CONFIRMED || status === OrderStatusObj.PROCESSING || status === OrderStatusObj.GLUS_UPDATED;
};

export const createSession = (
  tokenResponse: any,
  oldSession: any,
  // phoneNumber: string,
): SessionDTO => {
  return {
    // _mt: tokenResponse,
    _tk: tokenResponse.access_token,
    _rt: tokenResponse.refresh_token,

    // _ph: phoneNumber,
    _st: tokenResponse.status || oldSession?._st || 'activated',
    _at: tokenResponse.authType || oldSession?._at || 'otp',
    _lg: oldSession?._lg || 'en',
    _pu: oldSession?._pu,
    // _ml: tokenResponse.email || oldSession?._ml,

    _ve: oldSession?._ve ?? true,
    _vp: oldSession?._vp ?? true,
    _tc: oldSession?._tc || false,

    // _id: tokenResponse.juicyAppUniqueId || oldSession?._id,
    // _n1: tokenResponse.customerName || oldSession?._n1 || '',
    // _n2: tokenResponse.customerNameZh || oldSession?._n2 || '',
    _ti: tokenResponse.tier || tokenResponse.member_tier || oldSession?._ti,
  };
};

export const normalizeLang = (lang: string) => {
  if (lang.startsWith('en')) return 'en';
  if (lang.startsWith('zh')) return 'zh';
  return 'en'; // fallback
};